import { create } from "zustand";
import { ColumnState } from "ag-grid-community";
import { defaultTQC2080_R01Dto, TQC2080_R01Dto } from "./Dto_TQC2080_R01";

export interface TQC2080_R01Filter {
	year: string;
	searchAnalysisDt: string;
}

const getCurrentYear = (): string => String(new Date().getFullYear());

export const defaultTQC2080_R01Filter: TQC2080_R01Filter = {
	year: getCurrentYear(),
	searchAnalysisDt: "",
};

// ──────────────────────────────────────────────────
// DateRow : 좌측 날짜 목록 행 타입
// ──────────────────────────────────────────────────
export interface DateRow {
	analysisDt: string;
	inserted?: boolean;
	isModified?: boolean;
	valueIndex: number;
	orderIndex: number;
	orderIndexSeq: number;
}

export const defaultDateRow: DateRow = {
	analysisDt: "",
	inserted: true,
	isModified: false,
	valueIndex: 0,
	orderIndex: 0,
	orderIndexSeq: 0,
};

/** insert / remove 후 valueIndex / orderIndex 재정렬 */
const reindexDateRows = (rows: DateRow[]): DateRow[] =>
	rows.map((row, idx) => ({
		...row,
		valueIndex: idx + 1,
		orderIndex: idx + 1,
	}));

// ──────────────────────────────────────────────────
// Store 인터페이스
// ──────────────────────────────────────────────────
export interface Store_TQC2080_R01 {
	// 필터
	TQC2080_R01Filter: TQC2080_R01Filter;
	TQC2080_R01PastFilter: TQC2080_R01Filter;
	setTQC2080_R01Filter: (filter: Partial<TQC2080_R01Filter>) => void;
	setTQC2080_R01PastFilter: (filter: Partial<TQC2080_R01Filter>) => void;

	// 좌측 날짜 목록
	dateList: string[]; // Content.tsx 호환용 (API 원본 문자열 목록)
	dateRowList: DateRow[]; // DateList 그리드용 행 목록
	setDateList: (list: string[]) => void; // string[] → DateRow[] 자동 변환
	insertDateList: (rows: DateRow[], afterValueIndex: number) => void;
	removeDateList: (valueIndex: number) => void;
	updateDateList: (valueIndex: number, data: DateRow) => void;

	//  : 삭제 예정 날짜 목록 (analysisDt 기준)
	deletedDateList: string[];
	addDeletedDateList: (dates: string[]) => void;
	removeDeletedDateList: (dates?: string[]) => void;
	clearDeletedDateList: () => void;

	// 우측 그리드
	TQC2080_R01List: TQC2080_R01Dto[];
	setTQC2080_R01List: (list: TQC2080_R01Dto[]) => void;
	updateTQC2080_R01List: (valueIndex: number, data: TQC2080_R01Dto) => void;
	originalTQC2080_R01List: TQC2080_R01Dto[];
	setOriginalTQC2080_R01List: (list: TQC2080_R01Dto[]) => void;

	selectedTQC2080_R01: TQC2080_R01Dto | undefined;
	setSelectedTQC2080_R01: (item: TQC2080_R01Dto | undefined) => void;
	initSelectedTQC2080_R01: () => void;

	// 차트용 연간 데이터
	chartData: TQC2080_R01Dto[];
	setChartData: (list: TQC2080_R01Dto[]) => void;

	// 탭
	activeTab: "journal" | "chart";
	setActiveTab: (tab: "journal" | "chart") => void;

	// 컬럼 상태
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	// 전체 초기화
	initData: () => void;
}

// ──────────────────────────────────────────────────
// Store 구현
// ──────────────────────────────────────────────────
export const useTQC2080_R01Store = create<Store_TQC2080_R01>((set) => ({
	TQC2080_R01Filter: defaultTQC2080_R01Filter,
	TQC2080_R01PastFilter: defaultTQC2080_R01Filter,
	setTQC2080_R01Filter: (filter) =>
		set((state) => ({
			TQC2080_R01Filter: { ...state.TQC2080_R01Filter, ...filter },
		})),
	setTQC2080_R01PastFilter: (filter) =>
		set((state) => ({
			TQC2080_R01PastFilter: { ...state.TQC2080_R01PastFilter, ...filter },
		})),

	// ── 날짜 목록 ──────────────────────────────────
	dateList: [],
	dateRowList: [],

	/** Content.tsx에서 string[] 로 호출 → 내부에서 DateRow[] 로 변환 */
	setDateList: (list) => {
		const rows: DateRow[] = list.map((date, idx) => ({
			analysisDt: date,
			inserted: false,
			isModified: false,
			valueIndex: idx + 1,
			orderIndex: idx + 1,
			orderIndexSeq: 0,
		}));
		set({ dateList: list, dateRowList: rows });
	},

	/** + 버튼: afterValueIndex 행 바로 아래에 삽입 */
	insertDateList: (rows, afterValueIndex) =>
		set((state) => {
			const idx = state.dateRowList.findIndex((r) => r.valueIndex === afterValueIndex);
			const insertAt = idx >= 0 ? idx + 1 : state.dateRowList.length;
			const next = [
				...state.dateRowList.slice(0, insertAt),
				...rows,
				...state.dateRowList.slice(insertAt),
			];
			return { dateRowList: reindexDateRows(next) };
		}),

	/** - 버튼 (inserted 행 전용): valueIndex 기준 제거 후 재정렬 */
	removeDateList: (valueIndex) =>
		set((state) => {
			const next = state.dateRowList.filter((r) => r.valueIndex !== valueIndex);
			return { dateRowList: reindexDateRows(next) };
		}),

	/** analysisDt 셀 편집 시 단건 업데이트 */
	updateDateList: (valueIndex, data) =>
		set((state) => ({
			dateRowList: state.dateRowList.map((r) =>
				r.valueIndex === valueIndex ? { ...data } : r,
			),
		})),

	//  : 삭제 예정 날짜 목록
	deletedDateList: [],
	addDeletedDateList: (dates) =>
		set((state) => ({
			deletedDateList: [...state.deletedDateList, ...dates],
		})),
	removeDeletedDateList: (dates) =>
		set((state) => ({
			deletedDateList: dates
				? state.deletedDateList.filter((d) => !dates.includes(d))
				: [],
		})),
	clearDeletedDateList: () => set({ deletedDateList: [] }),

	// ── 우측 그리드 ────────────────────────────────
	TQC2080_R01List: [],
	setTQC2080_R01List: (list) =>
		set(() => {
			const newList = list.map((item, idx) => ({
				...defaultTQC2080_R01Dto,
				...item,
				valueIndex: idx + 1,
				orderIndex: idx + 1,
				shortage:
					Number(item.analysisVal ?? 0) - Number(item.centerVal ?? 0),
				inserted: false,
				isModified: false,
			}));
			return { TQC2080_R01List: newList };
		}),
	updateTQC2080_R01List: (valueIndex, data) =>
		set((state) => ({
			TQC2080_R01List: state.TQC2080_R01List.map((item) =>
				item.valueIndex === valueIndex
					? {
							...data,
							shortage:
								Number(data.analysisVal ?? 0) -
								Number(data.centerVal ?? 0),
						}
					: item,
			),
		})),
	originalTQC2080_R01List: [],
	setOriginalTQC2080_R01List: (list) =>
		set(() => {
			const newList = list.map((item, idx) => ({
				...defaultTQC2080_R01Dto,
				...item,
				valueIndex: idx + 1,
				orderIndex: idx + 1,
				shortage:
					Number(item.analysisVal ?? 0) - Number(item.centerVal ?? 0),
				inserted: false,
				isModified: false,
			}));
			return { originalTQC2080_R01List: newList };
		}),

	selectedTQC2080_R01: undefined,
	setSelectedTQC2080_R01: (item) => set({ selectedTQC2080_R01: item }),
	initSelectedTQC2080_R01: () => set({ selectedTQC2080_R01: undefined }),

	// ── 차트 ───────────────────────────────────────
	chartData: [],
	setChartData: (list) => set({ chartData: list }),

	// ── 탭 ─────────────────────────────────────────
	activeTab: "journal",
	setActiveTab: (tab) => set({ activeTab: tab }),

	// ── 컬럼 상태 ──────────────────────────────────
	columnState: null,
	setColumnState: (state) => set({ columnState: state }),

	scrollTop: null,
	setScrollTop: (top) => set({ scrollTop: top }),
	detailScrollTop: null,
	setDetailScrollTop: (top) => set({ detailScrollTop: top }),

	// ── 전체 초기화 ────────────────────────────────
	initData: () =>
		set({
			TQC2080_R01Filter: defaultTQC2080_R01Filter,
			TQC2080_R01PastFilter: defaultTQC2080_R01Filter,
			dateList: [],
			dateRowList: [],
			deletedDateList: [], //  
			TQC2080_R01List: [],
			originalTQC2080_R01List: [],
			selectedTQC2080_R01: undefined,
			chartData: [],
			activeTab: "journal",
			columnState: null,
			scrollTop: null,
			detailScrollTop: null,
		}),
}));
// ── 좌측 일자 그리드용 달력 팝업 상태 (PRD2010_R01 GridCalendar 패턴) ──
export interface Store_TQC2080_R01_GridCalendarState {
	TQC2080_R01GridCalendarState: {
		pickerVisible: boolean;
		pickerPosition: { top: number; left: number };
		targetRowIndex: number | null;
		targetField: string | null;
		targetDate: Date | undefined;
	};
	setTQC2080_R01GridCalendarState: (
		state: Partial<Store_TQC2080_R01_GridCalendarState["TQC2080_R01GridCalendarState"]>,
	) => void;
	initTQC2080_R01GridCalendarState: () => void;
}

export const useTQC2080_R01Store_GridCalendar = create<Store_TQC2080_R01_GridCalendarState>(
	(set) => ({
		TQC2080_R01GridCalendarState: {
			pickerVisible: false,
			pickerPosition: { top: 0, left: 0 },
			targetRowIndex: null,
			targetField: null,
			targetDate: undefined,
		},
		setTQC2080_R01GridCalendarState: (newState) =>
			set((state) => ({
				...state,
				TQC2080_R01GridCalendarState: {
					...state.TQC2080_R01GridCalendarState,
					...newState,
				},
			})),
		initTQC2080_R01GridCalendarState: () =>
			set((state) => ({
				...state,
				TQC2080_R01GridCalendarState: {
					pickerVisible: false,
					pickerPosition: { top: 0, left: 0 },
					targetRowIndex: null,
					targetField: null,
					targetDate: undefined,
				},
			})),
	}),
);

